import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

export interface IParkTicketType {
  type: string;
  title: string;
  description: string;
  fromPrice: number;   // ticket más barato del tipo (entre todos los días)
  fromDays: number;
  pins: IParkPin[];
  image: string;       // ilustración fija (Disney); vacía = se dibujan los pines
}

export interface IParkPin {
  x: number;
  y: number;
  scale: number;
  color: string;
  icon: 'mickey' | 'tree' | 'castle' | 'ring' | 'fort' | 'lifebuoy';
}

const BLUE = '#1b9ad6';
const GREEN = '#35b44a';
const PURPLE = '#b84fc9';
const ORANGE = '#f08a24';
const TEAL = '#1aa5b8';

const HOPPER_PINS: IParkPin[] = [
  { x: 52, y: 52, scale: 0.8, color: GREEN, icon: 'tree' },
  { x: 150, y: 70, scale: 0.8, color: PURPLE, icon: 'ring' },
  { x: 188, y: 70, scale: 0.8, color: ORANGE, icon: 'fort' },
  { x: 100, y: 40, scale: 1, color: BLUE, icon: 'castle' },
];

// Orden, textos e ilustración de los tipos de ticket de Octopus (tickets.options[].type).
export const PARK_TICKET_TYPES: { [key: string]: { title: string; description: string; order: number; pins: IParkPin[] } } = {
  base: {
    title: 'Un Parque por día',
    description: 'Visita un Parque Temático cada día de tu boleto.',
    order: 1,
    pins: [{ x: 100, y: 40, scale: 1.25, color: BLUE, icon: 'mickey' }],
  },
  water_park_sports: {
    title: 'Parques Acuáticos y Deportes',
    description: 'Visita un Parque Temático cada día en que tu ticket es válido. Además, disfruta de parques acuáticos y complejo deportivo.',
    order: 2,
    pins: [
      { x: 78, y: 38, scale: 1.2, color: BLUE, icon: 'mickey' },
      { x: 138, y: 72, scale: 0.9, color: TEAL, icon: 'lifebuoy' },
    ],
  },
  park_hopper: {
    title: 'Park Hopper',
    description: 'Visita varios Parques Temáticos cada día en que tu ticket es válido.',
    order: 3,
    pins: HOPPER_PINS,
  },
  park_hopper_plus: {
    title: 'Park Hopper Plus',
    description: 'Park Hopper con acceso adicional a parques acuáticos y complejo deportivo.',
    order: 4,
    pins: [...HOPPER_PINS, { x: 108, y: 84, scale: 0.8, color: TEAL, icon: 'lifebuoy' }],
  },
};

// Tipos de ticket de Universal Orlando (Full of Dreams): el tipo sale del nombre del ticket en Octopus.
const UNIVERSAL_TICKET_TYPES: { [key: string]: { title: string; description: string; order: number; pins: IParkPin[] } } = {
  base: {
    title: 'Un parque por día',
    description: 'Entrada a un parque de Universal Orlando por cada día de tu ticket.',
    order: 1,
    pins: [{ x: 100, y: 40, scale: 1.25, color: BLUE, icon: 'castle' }],
  },
  park_to_park: {
    title: 'Park-to-Park',
    description: 'Visita más de un parque de Universal Orlando el mismo día.',
    order: 2,
    pins: [
      { x: 70, y: 44, scale: 1, color: BLUE, icon: 'castle' },
      { x: 138, y: 44, scale: 1, color: ORANGE, icon: 'fort' },
    ],
  },
  express: {
    title: 'Universal Express',
    description: 'Ticket con acceso Express para evitar las filas en las atracciones participantes.',
    order: 3,
    pins: [{ x: 100, y: 40, scale: 1.25, color: PURPLE, icon: 'ring' }],
  },
  epic: {
    title: 'Epic Universe',
    description: 'Entrada a Epic Universe, el parque más nuevo de Universal Orlando.',
    order: 4,
    pins: [{ x: 100, y: 40, scale: 1.25, color: GREEN, icon: 'castle' }],
  },
};

/** Texto e ilustración de un tipo de ticket según el parque de la búsqueda (DISNEY, UNIVERSAL). */
export function parkTicketInfo(hotelType: string, type: string) {
  const table = String(hotelType || '').toUpperCase() === 'UNIVERSAL' ? UNIVERSAL_TICKET_TYPES : PARK_TICKET_TYPES;
  return table[type];
}

/**
 * Paso "Tickets y adicionales": una tarjeta por tipo de ticket con su precio "desde" (ticket más barato
 * entre los días disponibles). El precio es el del ticket solo (ticketPrice, o ticketPriceProf si el back
 * aplicó rentabilidad), no el total con la habitación.
 */
@Component({
  selector: 'usb-park-tickets',
  templateUrl: './park-tickets.component.html',
  styleUrls: ['./park-tickets.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkTicketsComponent implements OnChanges {
  @Input() options: any[] = [];
  @Input() hotelType = 'DISNEY';
  @Input() selectedType: string | null = null; // tipo de ticket, o 'none' para reservar solo el hotel
  @Input() allowNone = true;
  @Input() adults = 0;
  @Input() children = 0;
  @Input() sourceCurrency = 'USD';
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() buttonColorActive: string;
  @Input() buttonColorActiveDark: string;

  @Output() choose = new EventEmitter<string>();

  types: IParkTicketType[] = [];
  expanded: { [type: string]: boolean } = {};

  ngOnChanges(): void {
    const byType = new Map<string, IParkTicketType>();
    (this.options || []).forEach((o: any) => {
      const price = Number(o.ticketPriceProf ?? o.ticketPrice ?? 0);
      const current = byType.get(o.type);
      if (!current || price < current.fromPrice) {
        const info = parkTicketInfo(this.hotelType, o.type);
        byType.set(o.type, {
          type: o.type,
          title: info?.title || o.type,
          description: info?.description || o.name,
          fromPrice: price,
          fromDays: Number(o.days),
          pins: info?.pins || [],
          image: this.imageOf(o.type),
        });
      }
    });
    this.types = Array.from(byType.values())
      .sort((a, b) => (parkTicketInfo(this.hotelType, a.type)?.order || 99) - (parkTicketInfo(this.hotelType, b.type)?.order || 99));
  }

  // Ilustraciones de Disney exportadas del diseño (assets/img/parks/disney/tickets/<tipo>.png).
  private imageOf(type: string): string {
    const disney = String(this.hotelType || '').toUpperCase() !== 'UNIVERSAL' && !!PARK_TICKET_TYPES[type];
    return disney ? `/assets/img/parks/disney/tickets/${type}.png` : '';
  }

  toggle(type: string): void {
    this.expanded[type] = !this.expanded[type];
  }

  trackByType(_: number, type: IParkTicketType): string {
    return type.type;
  }
}
